import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Switch, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import theme from './theme';
import { colors } from './theme/tokens';
import Navbar from './components/Navbar';
import FloatingActions from './components/FloatingActions';
import SkipLink from './components/SkipLink';
import ConsentBanner from './components/ConsentBanner';
import Home from './pages/Home';
import { installLeadClickTracking } from './utils/analytics';

const About = lazy(() => import('./pages/About'));
const Plumbing = lazy(() => import('./pages/Plumbing'));
const Renovations = lazy(() => import('./pages/Renovations'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const Contact = lazy(() => import('./pages/Contact'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Testimonials = lazy(() => import('./pages/Testimonials'));
const Locations = lazy(() => import('./pages/Locations'));
const Privacy = lazy(() => import('./pages/Privacy'));
const NotFound = lazy(() => import('./pages/NotFound'));

function ScrollManager() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const scrollToTarget = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return true;
        }
        return false;
      };

      if (!scrollToTarget()) {
        const timer = setTimeout(scrollToTarget, 150);
        return () => clearTimeout(timer);
      }
      return;
    }

    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
}

const RouteFallback = () => (
  <Box
    role="status"
    aria-live="polite"
    aria-label="Loading page"
    sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center', backgroundColor: colors.bg }}
  >
    <CircularProgress size={28} sx={{ color: colors.textMuted }} />
  </Box>
);

function App() {
  useEffect(() => installLeadClickTracking(), []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ScrollManager />
        <SkipLink />
        <Navbar />
        <Box component="main" id="main-content" tabIndex={-1} sx={{ outline: 'none' }}>
          <Suspense fallback={<RouteFallback />}>
            <Switch>
              <Route exact path="/" component={Home} />
              <Route path="/about" component={About} />
              <Route path="/plumbing" component={Plumbing} />
              <Route path="/renovations" component={Renovations} />
              <Route path="/maintenance" component={Maintenance} />
              <Route path="/contact" component={Contact} />
              <Route path="/portfolio" component={Portfolio} />
              <Route path="/gallery" component={Gallery} />
              <Route path="/testimonials" component={Testimonials} />
              <Route path="/locations" component={Locations} />
              <Route path="/privacy" component={Privacy} />
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </Box>
        <FloatingActions />
        <ConsentBanner />
      </Router>
    </ThemeProvider>
  );
}

export default App;
